import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  fetchSharepoint,
  fetchUnreadFeedbackSubmissionCount,
  fetchRequestSharesApi,
  fetchBOMSharesApi,
  fetchGuestRequestSharesApi,
  fetchArticleSharesApi,
  type UnreadFeedbackSubmissionCount,
} from '../api/sharepointApi'
import { type PaginatedResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import { type Sharepoint } from '../../Item/utils/types'
import { sharepointKeys } from '../utils/queryKeys'

/**
 * Hook to fetch a sharepoint
 */
export function useSharepoint(
  sharepointId: string | number,
  enabled = true
): UseQueryResult<any, Error> {
  return useQuery({
    queryKey: sharepointKeys.detail(sharepointId),
    queryFn: () => fetchSharepoint(sharepointId),
    enabled: enabled && Boolean(sharepointId),
  })
}

/**
 * Hook to fetch unread feedback submission count
 */
export function useUnreadFeedbackSubmissionCount(
  enabled = true
): UseQueryResult<UnreadFeedbackSubmissionCount, Error> {
  return useQuery({
    queryKey: ['sharepoint', 'unread-feedback-count'],
    queryFn: fetchUnreadFeedbackSubmissionCount,
    enabled,
  })
}

/**
 * Hook to fetch request shares (sharepoints for requests)
 */
export function useRequestShares(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Sharepoint>, Error> {
  return useQuery({
    queryKey: [
      ...sharepointKeys.list({
        page: options.page,
        pageSize: options.pageSize,
        filters: { ...options.filters, type: 'request_shares' },
      }),
      'expand',
      options.expand,
    ],
    queryFn: () => fetchRequestSharesApi(options),
    enabled,
  })
}

/**
 * Hook to fetch BOM shares (sharepoints for BOMs)
 */
export function useBOMShares(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Sharepoint>, Error> {
  return useQuery({
    queryKey: [
      ...sharepointKeys.list({
        page: options.page,
        pageSize: options.pageSize,
        filters: { ...options.filters, type: 'bom_shares' },
      }),
      'expand',
      options.expand,
    ],
    queryFn: () => fetchBOMSharesApi(options),
    enabled,
  })
}

/**
 * Hook to fetch guest request shares (sharepoints for guest requests)
 */
export function useGuestRequestShares(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Sharepoint>, Error> {
  return useQuery({
    queryKey: [
      ...sharepointKeys.list({
        page: options.page,
        pageSize: options.pageSize,
        filters: { ...options.filters, type: 'guest_request_shares' },
      }),
      'expand',
      options.expand,
    ],
    queryFn: () => fetchGuestRequestSharesApi(options),
    enabled,
  })
}

/**
 * Hook to fetch article shares (sharepoints for articles)
 */
export function useArticleShares(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Sharepoint>, Error> {
  return useQuery({
    queryKey: [
      ...sharepointKeys.list({
        page: options.page,
        pageSize: options.pageSize,
        filters: { ...options.filters, type: 'article_shares' },
      }),
      'expand',
      options.expand,
    ],
    queryFn: () => fetchArticleSharesApi(options),
    enabled,
  })
}

